import { Control, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/core/components/ui/form";
import { Input } from "src/core/components/ui/input";
import { cn } from "src/core/lib/utils";

type FormInlineInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>["type"];
  placeholder?: string;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  control?: Control<T>;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export function FormInlineInput<T extends FieldValues>({
  name,
  label,
  type = "text",
  placeholder,
  required = false,
  className = "w-[300px]",
  wrapperClassName = "flex items-center justify-between w-full",
  control,
  disabled = false,
  onChange,
}: FormInlineInputProps<T>) {
  const { t } = useTranslation();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const isInvalid = fieldState.invalid;

        return (
          <FormItem>
            <div className={wrapperClassName}>
              <FormLabel required={required} className={cn("shrink-0", isInvalid && "text-alert")}>
                {label}
              </FormLabel>
              <FormControl>
                <Input
                  type={type}
                  variant="inline"
                  className={cn(className, isInvalid && "border-alert")}
                  placeholder={placeholder ?? t("global.form.input.placeholder")}
                  disabled={disabled}
                  {...field}
                  value={field.value ?? ""}
                  onChange={onChange ?? field.onChange}
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
